import { type DeploymentStrategy, supportedChangeIpAddresses } from '@emma/core/consts';
import {
  coreVersions,
  deprecatedCoreVersions,
  latestStableCoreVersion,
  supportedCoreVersions,
  usesOTel,
} from '@emma/core/core';
import { getCompatibleOsVersionsForDevice, isOsVersionAtLeast } from '@emma/core/device';
import {
  latestStableMinorOsVersion,
  type OSVersion,
  type OSVersionWithLatest,
} from '@emma/core/os';
import {
  Box,
  Button,
  CloseButton,
  Dropdown,
  Flex,
  Grid,
  IconButton,
  InlineNotification,
  Input,
  Link,
  Text,
  Tooltip,
  useToast,
} from '@lego/klik-ui';
import {
  ArrowUpSolid,
  CloudAvailableBold,
  CloudUnavailableBold,
  Edit,
  Refresh,
  StatusCircleCheck,
  StatusCircleExclamation,
  StatusInprogress,
} from '@lego/klik-ui/icons';
import type { InferRequestType } from 'hono/client';
import { useCallback, useMemo, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { spin } from '@/animations/spin';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { type DetailSectionProps, DetailsBox } from '@/components/common/details/DetailsBox';
import { EditButtons } from '@/components/common/EditButtons';
import {
  ImmediateStrategyWarning,
  isImmediateStrategy,
} from '@/components/common/ImmediateStrategyWarning';
import { type ApiClient, useMutation } from '@/hooks/api';
import {
  type DeviceNetworkConfigResponse,
  type DeviceResponse,
  type PatchDeviceRequest,
  useDeviceNetworkConfigs,
  useUpdateDevice,
} from '@/hooks/device';
import { useOsRolloutWindows } from '@/hooks/rollout_windows';
import {
  type NetworkConfigResponse,
  useCanOperate,
  useCanWrite,
  useWorkspace,
  useWorkspaceNetworkConfigs,
} from '@/hooks/workspace';
import { dateTimeFormat } from '@/utility/date';
import {
  deploymentStrategyDocURL,
  emmaOs1ChangelogURL,
  emmaOs2ChangelogURL,
  emmaOs3ChangelogURL,
  firewallConfigurationDocURL,
} from '@/utility/external_urls';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';
import { ToastFactory } from '@/utility/toast_factory';
import { Loader } from '../common';
import { CopyableText } from '../common/CopyableText';
import { DocumentationLink } from '../common/DocumentationLink';
import { FleetNameBadge } from '../fleet/FleetBadge';
import { WorkspaceTextLink } from '../workspace/WorkspaceTextLink';
import { DeviceDeploymentBadge } from './DeviceDeploymentBadge';
import { DeviceInstallation } from './DeviceInstallation';
import { DeviceNetworkInterfaces, type InterfaceConfigInfo } from './DeviceNetworkInterfaces';
import { ReportedCoreVersion } from './ReportedCoreVersion';

type Props = {
  device: DeviceResponse;
  refresh: () => void;
  // TODO(OSTree-Upgrade): This should be removed once all devices have been upgraded to Bootc
  onUpgradeOs?: () => void;
};

type RolloutWindowWarningDevice = {
  deploymentStrategy: DeploymentStrategy;
  deployments: readonly unknown[];
  fleets: readonly unknown[];
};

export const shouldShowRolloutWindowWarning = (device: RolloutWindowWarningDevice) =>
  device.deploymentStrategy === 'SCHEDULE' &&
  device.deployments.length === 0 &&
  device.fleets.length > 0;

export const ROLLOUT_WINDOW_WARNING_DISMISSED_DEVICES_KEY =
  'dismissedRolloutWindowWarningDeviceIds';
const MAX_DISMISSED_ROLLOUT_WINDOW_WARNING_DEVICE_IDS = 200;

const getDismissedRolloutWindowWarningDeviceIds = (): number[] => {
  const dismissedDeviceIds = localStorage.getItem(ROLLOUT_WINDOW_WARNING_DISMISSED_DEVICES_KEY);
  if (!dismissedDeviceIds) return [];

  try {
    const parsed = JSON.parse(dismissedDeviceIds);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is number => typeof id === 'number');
  } catch {
    return [];
  }
};

type OsRolloutWarningDevice = Pick<DeviceResponse, 'deploymentStrategy' | 'state' | 'os'>;

export const isOsUpdatePending = (os: DeviceResponse['os']) => {
  if (os?.name !== 'emmaOS' || !os.desiredVersion) return false;
  return isBootcDevice(os.version)
    ? !isBootcOsSync(os.desiredVersion as OSVersionWithLatest, os.version as OSVersion)
    : os.desiredVersion !== 'latest' && os.desiredVersion !== os.version;
};

export const isOsRolloutWindowWarningCandidate = (device: OsRolloutWarningDevice) =>
  device.state === 'PROVISIONED' &&
  (device.deploymentStrategy === 'SCHEDULE' ||
    device.deploymentStrategy === 'IMMEDIATE_APPLICATION_ONLY') &&
  isOsUpdatePending(device.os);

export const shouldShowOsRolloutWindowWarning = (
  device: OsRolloutWarningDevice,
  osRolloutWindows: readonly { status: string }[] | undefined
) =>
  isOsRolloutWindowWarningCandidate(device) &&
  osRolloutWindows !== undefined &&
  !osRolloutWindows.some((window) => window.status === 'ACTIVE');

const DEPLOYMENT_STRATEGY_OPTIONS = [
  { value: 'SCHEDULE', label: 'Schedule' },
  {
    value: 'IMMEDIATE_APPLICATION_ONLY',
    label: 'Immediate (Application Only)',
  },
  { value: 'IMMEDIATE', label: 'Immediate' },
];

type PostDeviceAction = ApiClient['v1']['devices'][':deviceId']['actions']['$post'];
type DeviceActionBody = InferRequestType<PostDeviceAction>['json'];
type ChangeIpValue = (typeof supportedChangeIpAddresses)[number];

const originalChangeIpRuleset = 'EMMA (.222, .223, .240, .253)';
const additionalChangeIpRuleset = 'EMMA (.250, .251, .252, .253)';

const changeIpRulesetByAddress = {
  '192.168.1.222': originalChangeIpRuleset,
  '192.168.1.223': originalChangeIpRuleset,
  '192.168.1.240': originalChangeIpRuleset,
  '192.168.1.250': additionalChangeIpRuleset,
  '192.168.1.251': additionalChangeIpRuleset,
  '192.168.1.252': additionalChangeIpRuleset,
  '192.168.1.253': null,
} satisfies Record<ChangeIpValue, string | null>;

const getChangeIpRuleset = (ip: string) => changeIpRulesetByAddress[ip as ChangeIpValue];

export const getChangeIpRulesetGuidance = (ip: string) => {
  if (ip === '192.168.1.253') return `${ip} is supported by either EMMA FARO ruleset.`;

  const ruleset = getChangeIpRuleset(ip);
  return ruleset
    ? `${ip} requires the ${ruleset} FARO ruleset.`
    : 'Select a supported IP address to see the required FARO ruleset.';
};

const getChangeIpRulesetLabel = (ip: string) => {
  const ruleset = getChangeIpRuleset(ip);
  return ip === '192.168.1.253' ? 'Either EMMA FARO ruleset' : (ruleset ?? 'Check FARO ruleset');
};

const CORE_VERSION_OPTIONS = (
  import.meta.env.VITE_ENV === 'dev' ? coreVersions : supportedCoreVersions
).map((version) => ({
  value: version,
  label: version,
}));

const getDeploymentStrategyLabel = (value: string): string => {
  const option = DEPLOYMENT_STRATEGY_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? value;
};

const isDeprecatedCoreVersion = (version: string | null): boolean => {
  if (!version) return false;
  return (deprecatedCoreVersions as readonly string[]).includes(version);
};

const hasNewerCoreVersion = (version: string | null): boolean => {
  if (!version) return false;
  if (isDeprecatedCoreVersion(version)) return false;
  return parseInt(version.slice(1), 10) < parseInt(latestStableCoreVersion.slice(1), 10);
};

const validateDeviceName = (name: string): string | null => {
  if (name.length < 3 || name.length > 100) {
    return 'Name must be between 3 and 100 characters';
  }
  return null;
};

const isBootcOsSync = (desired: OSVersionWithLatest, version: OSVersion) => {
  if (desired === 'latest') {
    return latestStableMinorOsVersion(version) === version;
  } else {
    return desired === version;
  }
};

const isBootcDevice = (version: string) => Number(version.split('.')[0]) >= 2;

const getOsChangelogURL = (version: string): string => {
  const major = version.split('.')[0];
  switch (major) {
    case '2':
      return emmaOs2ChangelogURL;
    case '3':
      return emmaOs3ChangelogURL;
    default:
      return emmaOs1ChangelogURL;
  }
};

type OsInfoBodyProps = {
  device: DeviceResponse;
  canOperate: boolean;
  canWrite: boolean;
  editingField: string | null;
  editedValue: string;
  setEditedValue: (v: string) => void;
  osVersionOptions: { value: string; label: string }[];
  isSaving: boolean;
  handleCancelEdit: () => void;
  handleStartEdit: (key: string, value: string) => void;
  handleSave: (field: keyof PatchDeviceRequest) => Promise<void>;
  onUpgradeOs?: () => void;
};

const OsUpgradeButton = ({
  device,
  onUpgradeOs,
}: {
  device: DeviceResponse;
  onUpgradeOs: () => void;
}) => {
  const majorVersion = Number.parseInt(device.core.reportedVersion?.replace(/^v/, '') ?? '0', 10);
  const isCoreCompatible = !Number.isNaN(majorVersion) && majorVersion >= 15;
  const osParts = (device.os?.version ?? '').split('.').map(Number);
  const isOsCompatible =
    osParts.length >= 3 &&
    osParts.every((n) => !Number.isNaN(n)) &&
    (osParts[0] > 1 ||
      (osParts[0] === 1 && osParts[1] > 4) ||
      (osParts[0] === 1 && osParts[1] === 4 && osParts[2] >= 8));
  const isProvisioned = device.state === 'PROVISIONED';
  const isCompatible = isProvisioned && isCoreCompatible && isOsCompatible;
  const tooltipLabel = !isProvisioned
    ? 'Device has not been provisioned yet.'
    : !isCoreCompatible
      ? 'OS upgrade requires core version 15 or above. Please update the core version first.'
      : !isOsCompatible
        ? 'OS upgrade requires OS version 1.4.8 or above.'
        : 'Migrate to next major emmaOS version';

  return (
    <Tooltip label={tooltipLabel}>
      <Button
        colorScheme="warning"
        isDisabled={!isCompatible}
        ml={2}
        onClick={onUpgradeOs}
        size="xs"
        variant="outline"
      >
        <ArrowUpSolid mr={1} /> Upgrade
      </Button>
    </Tooltip>
  );
};

const OsInfoBody = ({
  device,
  canOperate,
  canWrite,
  editingField,
  editedValue,
  setEditedValue,
  osVersionOptions,
  isSaving,
  handleCancelEdit,
  handleStartEdit,
  handleSave,
  onUpgradeOs,
}: OsInfoBodyProps) => {
  if (!device.os) {
    return <>Unknown</>;
  }

  const os = device.os;

  if (os.name !== 'emmaOS') {
    return (
      <>
        {os.name} {os.version}
      </>
    );
  }

  if (isBootcDevice(os.version)) {
    if (canOperate && editingField === 'desiredOsVersion') {
      return (
        <Flex align="center" gap={2}>
          <Dropdown
            onChange={(val: string) => setEditedValue(val)}
            size="sm"
            value={[editedValue]}
            width={200}
          >
            {osVersionOptions.map((option) => (
              <Dropdown.Option key={option.value} value={option.value}>
                {option.label}
              </Dropdown.Option>
            ))}
          </Dropdown>
          <EditButtons
            editLabel="Edit Desired OS Version"
            isEditing
            isSaving={isSaving}
            onCancel={handleCancelEdit}
            onEdit={() => handleStartEdit('desiredOsVersion', os.desiredVersion)}
            onSave={async () => await handleSave('desiredOsVersion')}
            saveDisabled={editedValue === os.desiredVersion}
          />
        </Flex>
      );
    }
    return (
      <Flex align="center" gap={2}>
        <Link as={ReactRouterLink} to={getOsChangelogURL(os.version)}>
          {os.name} {os.version}
        </Link>
        {isBootcOsSync(os.desiredVersion as OSVersionWithLatest, os.version as OSVersion) ? (
          <Tooltip
            label={`The device is running the desired OS version. Desired: ${os.desiredVersion}`}
          >
            <StatusCircleCheck boxSize={5} color="green" mb={0.5} ml={2} />
          </Tooltip>
        ) : (
          <Tooltip
            label={`There is another desired OS version: ${os.desiredVersion}. If this is latest it might take a day for device to acknowledge the new update.`}
          >
            <StatusInprogress boxSize={5} color="grey" mb={0.5} ml={2} />
          </Tooltip>
        )}
        {canOperate && (
          <EditButtons
            editLabel="Edit Desired OS Version"
            isEditing={false}
            isSaving={isSaving}
            onCancel={handleCancelEdit}
            onEdit={() => handleStartEdit('desiredOsVersion', os.desiredVersion)}
            onSave={async () => await handleSave('desiredOsVersion')}
          />
        )}
      </Flex>
    );
  }

  // Non-bootc emmaOS (1.x) - no edit button
  return (
    <Flex align="center" gap={2}>
      <Link as={ReactRouterLink} to={getOsChangelogURL(os.version)}>
        {os.name} {os.version}
      </Link>
      {os.desiredVersion === os.version ? (
        <Tooltip label={'The device is running the latest OS version.'}>
          <StatusCircleCheck boxSize={5} color="green" mb={0.5} ml={2} />
        </Tooltip>
      ) : (
        <Tooltip label={`There is a new OS version available: ${os.name} ${os.desiredVersion}`}>
          <StatusInprogress boxSize={5} color="grey" mb={0.5} ml={2} />
        </Tooltip>
      )}
      {/* TODO(OSTree-Upgrade): This should be removed once all devices have been upgraded to Bootc */}
      {canWrite && onUpgradeOs && <OsUpgradeButton device={device} onUpgradeOs={onUpgradeOs} />}
    </Flex>
  );
};

type FieldEditState = {
  editingField: string | null;
  editedValue: string;
  setEditedValue: (v: string) => void;
  isSaving: boolean;
  handleStartEdit: (key: string, value: string) => void;
  handleCancelEdit: () => void;
  handleSave: (field: keyof PatchDeviceRequest) => Promise<void>;
};

const useDeviceFieldEdit = (device: DeviceResponse, refresh: () => void) => {
  const toast = useToast();
  const { mutate } = useSWRConfig();
  const updateDevice = useUpdateDevice(device.id);
  const cacheKey = `devices/${device.id}`;

  const postIpUpdate = useMutation<unknown, { ip: ChangeIpValue }>(
    `devices/${device.id}/change-ip`,
    (client, { ip }) =>
      client.v1.devices[':deviceId'].actions.$post({
        param: { deviceId: device.id.toString() },
        json: { action: 'CHANGE-IP', ip } satisfies DeviceActionBody,
      }),
    () => {}
  );

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedValue, setEditedValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = useCallback((key: string, currentValue: string) => {
    setEditedValue(currentValue);
    setEditingField(key);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditedValue('');
    setEditingField(null);
  }, []);

  const handleSave = useCallback(
    async (fieldName: keyof PatchDeviceRequest, value?: string) => {
      const valueToSave = value ?? editedValue;
      if (fieldName === 'name') {
        const validationError = validateDeviceName(valueToSave);
        if (validationError) {
          toast(ToastFactory.validationError(validationError));
          return;
        }
      }
      setIsSaving(true);
      try {
        const updatedDevice = await updateDevice({
          [fieldName]: valueToSave,
        } as PatchDeviceRequest);
        // We get the device response from patch call, no need to refetch
        await mutate(cacheKey, updatedDevice, { revalidate: false });
        refresh();
        setEditingField(null);
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      } finally {
        setIsSaving(false);
      }
    },
    [editedValue, mutate, updateDevice, cacheKey, toast, refresh]
  );

  const handleIpSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const result = await postIpUpdate({ ip: editedValue as ChangeIpValue });
      if (result === undefined) return;
      refresh();
      setEditingField(null);
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsSaving(false);
    }
  }, [editedValue, postIpUpdate, refresh]);

  return {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleIpSave,
  };
};

const useFirewallLocationRefresh = (deviceId: number, refresh: () => void) => {
  const [isRefreshingLocationData, setIsRefreshingLocationData] = useState(false);

  const refreshLocationData = useMutation<unknown, { deviceId: number }>(
    'device-refresh-location-data',
    (client, { deviceId: id }) =>
      client.v1.devices[':deviceId'].actions.$post({
        param: { deviceId: id.toString() },
        json: { action: 'REFRESH-LOCATION-DATA' },
      })
  );

  const refreshFirewallLocationData = useCallback(async () => {
    if (isRefreshingLocationData) return;
    setIsRefreshingLocationData(true);
    try {
      await refreshLocationData({ deviceId });
      refresh();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsRefreshingLocationData(false);
    }
  }, [isRefreshingLocationData, refreshLocationData, deviceId, refresh]);

  return { isRefreshingLocationData, refreshFirewallLocationData };
};

const buildConfigsByInterface = (
  deviceNetworkConfigs: DeviceNetworkConfigResponse[] | undefined,
  networkConfigs: NetworkConfigResponse[] | undefined
): Map<string, InterfaceConfigInfo> | undefined => {
  if (!deviceNetworkConfigs || !networkConfigs) return undefined;
  const configById = new Map(networkConfigs.map((c) => [c.id, c]));
  const result = new Map<string, InterfaceConfigInfo>();
  for (const assignment of deviceNetworkConfigs) {
    const config = configById.get(assignment.networkConfigId);
    if (config) {
      result.set(config.interfaceName, { config, assignment });
    }
  }
  return result;
};

const getDeviceDashboardId = (reportedVersion: string | null | undefined): string =>
  reportedVersion && usesOTel(reportedVersion)
    ? 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
    : '43f14ea0-c599-11ee-94de-c9c1545cf3b3';

const DeviceNameHeader = ({
  device,
  canOperate,
  editState,
}: {
  device: DeviceResponse;
  canOperate: boolean;
  editState: FieldEditState;
}) => {
  const {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
  } = editState;

  if (canOperate && editingField === 'name') {
    return (
      <Flex align="center" gap={2} mb={4}>
        <Input
          autoFocus
          maxWidth="400px"
          onChange={(e) => setEditedValue(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter') {
              await handleSave('name');
            } else if (e.key === 'Escape') {
              handleCancelEdit();
            }
          }}
          size="sm"
          value={editedValue}
        />
        <EditButtons
          editLabel="Edit Name"
          isEditing
          isSaving={isSaving}
          onCancel={handleCancelEdit}
          onEdit={() => handleStartEdit('name', device.name)}
          onSave={async () => await handleSave('name')}
          saveDisabled={device.name === editedValue}
        />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={2} mb={4}>
      <Text as="h3" fontSize="xl" fontWeight="bold">
        {device.name}
      </Text>
      {canOperate && (
        <IconButton
          aria-label="Edit name"
          color="LinkText"
          icon={<Edit />}
          onClick={() => handleStartEdit('name', device.name)}
          size="xs"
          variant="solid"
        />
      )}
    </Flex>
  );
};

const DesiredCoreVersionField = ({
  device,
  canWrite,
  editState,
}: {
  device: DeviceResponse;
  canWrite: boolean;
  editState: FieldEditState;
}) => {
  const {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
  } = editState;

  if (canWrite && editingField === 'desiredCoreVersion') {
    return (
      <Flex align="center" gap={2}>
        <Dropdown
          onChange={(val: string) => setEditedValue(val)}
          size="sm"
          value={[editedValue]}
          width={150}
        >
          {CORE_VERSION_OPTIONS.map((option) => (
            <Dropdown.Option key={option.value} value={option.value}>
              {option.label}
            </Dropdown.Option>
          ))}
        </Dropdown>
        <EditButtons
          editLabel="Edit Desired Core Version"
          isEditing
          isSaving={isSaving}
          onCancel={handleCancelEdit}
          onEdit={() => handleStartEdit('desiredCoreVersion', device.core.desiredVersion)}
          onSave={async () => await handleSave('desiredCoreVersion')}
          saveDisabled={editedValue === device.core.desiredVersion}
        />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={2}>
      <Box as="span">{device.core.desiredVersion}</Box>
      {isDeprecatedCoreVersion(device.core.desiredVersion) && (
        <Tooltip label="This core version is deprecated. Please update to a supported version.">
          <StatusCircleExclamation boxSize={5} color="red" />
        </Tooltip>
      )}
      {hasNewerCoreVersion(device.core.desiredVersion) && (
        <Tooltip label={`A newer core version is available: ${latestStableCoreVersion}`}>
          <Box as="span" color="light-blue.600">
            <ArrowUpSolid boxSize={4} />
          </Box>
        </Tooltip>
      )}
      {canWrite && (
        <EditButtons
          editLabel="Edit Desired Core Version"
          isEditing={false}
          isSaving={isSaving}
          onCancel={handleCancelEdit}
          onEdit={() => handleStartEdit('desiredCoreVersion', device.core.desiredVersion)}
          onSave={async () => await handleSave('desiredCoreVersion')}
        />
      )}
    </Flex>
  );
};

const DeploymentStrategyField = ({
  device,
  canWrite,
  editState,
}: {
  device: DeviceResponse;
  canWrite: boolean;
  editState: FieldEditState;
}) => {
  const {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
  } = editState;

  if (canWrite && editingField === 'deploymentStrategy') {
    return (
      <Flex align="center" gap={2}>
        <Dropdown
          onChange={(val: string) => setEditedValue(val)}
          size="sm"
          value={[editedValue]}
          width={250}
        >
          {DEPLOYMENT_STRATEGY_OPTIONS.map((option) => (
            <Dropdown.Option key={option.value} value={option.value}>
              {option.label}
            </Dropdown.Option>
          ))}
        </Dropdown>
        <EditButtons
          editLabel="Edit Deployment Strategy"
          isEditing
          isSaving={isSaving}
          onCancel={handleCancelEdit}
          onEdit={() => handleStartEdit('deploymentStrategy', device.deploymentStrategy)}
          onSave={async () => await handleSave('deploymentStrategy')}
          saveDisabled={editedValue === device.deploymentStrategy}
        />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap={2}>
      {isImmediateStrategy(device.deploymentStrategy) && <ImmediateStrategyWarning />}
      <Box as="span">{getDeploymentStrategyLabel(device.deploymentStrategy)}</Box>
      {canWrite && (
        <EditButtons
          editLabel="Edit Deployment Strategy"
          isEditing={false}
          isSaving={isSaving}
          onCancel={handleCancelEdit}
          onEdit={() => handleStartEdit('deploymentStrategy', device.deploymentStrategy)}
          onSave={async () => await handleSave('deploymentStrategy')}
        />
      )}
    </Flex>
  );
};

const DesiredIpField = ({
  device,
  canOperate,
  editState,
  handleIpSave,
}: {
  device: DeviceResponse;
  canOperate: boolean;
  editState: FieldEditState;
  handleIpSave: () => Promise<void>;
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const { editingField, editedValue, setEditedValue, isSaving, handleStartEdit, handleCancelEdit } =
    editState;

  if (canOperate && editingField === 'ip') {
    const rulesetLabel = getChangeIpRulesetLabel(editedValue);
    const rulesetHeading =
      editedValue === '192.168.1.253' ? 'Compatible FARO ruleset' : 'Required FARO ruleset';

    return (
      <>
        <ConfirmationDialog
          body={
            <Box>
              <Text mb={3}>
                You are about to change the desired IP to{' '}
                <Text as="span" fontFamily="mono" fontWeight="semibold">
                  {editedValue}
                </Text>
                .
              </Text>
              <Text color="gray.600" fontSize="sm" fontWeight="semibold" mb={1}>
                {rulesetHeading}
              </Text>
              <Text color="orange.700" fontWeight="semibold" mb={3}>
                {rulesetLabel}
              </Text>
              <Text>
                Verify that the matching ruleset is applied before continuing. Without it, the
                device may lose EMMA connectivity.
              </Text>
            </Box>
          }
          header="Apply desired IP change?"
          isOpen={showConfirmationDialog}
          onClose={() => setShowConfirmationDialog(false)}
          onConfirmed={async () => {
            setShowConfirmationDialog(false);
            await handleIpSave();
          }}
        />
        <Flex align="center" flexWrap="wrap" gap={2}>
          <Dropdown
            onChange={(val: string) => setEditedValue(val)}
            size="sm"
            value={[editedValue]}
            width={140}
          >
            {supportedChangeIpAddresses.map((ip) => (
              <Dropdown.Option key={ip} value={ip}>
                {ip}
              </Dropdown.Option>
            ))}
          </Dropdown>
          <EditButtons
            editLabel="Edit Desired IP Address"
            isEditing
            isSaving={isSaving}
            onCancel={handleCancelEdit}
            onEdit={() => handleStartEdit('ip', device.desiredIp ?? supportedChangeIpAddresses[0])}
            onSave={() => setShowConfirmationDialog(true)}
            saveDisabled={!editedValue || editedValue === device.desiredIp}
          />
          <Flex align="center" background="warning.50" borderRadius="md" gap={2} px={3} py={2}>
            <StatusCircleExclamation boxSize={5} color="yellow.500" />
            <Box>
              <Text
                color="gray.600"
                fontSize="xs"
                fontWeight="bold"
                lineHeight="shorter"
                textTransform="uppercase"
              >
                {rulesetHeading}
              </Text>
              <Text fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
                {rulesetLabel}
              </Text>
            </Box>
            <Link fontSize="sm" href={firewallConfigurationDocURL} isExternal whiteSpace="nowrap">
              View guide
            </Link>
          </Flex>
        </Flex>
      </>
    );
  }

  return (
    <Flex align="center" gap={2}>
      <Box as="span">{device.desiredIp ?? 'Not set'}</Box>
      {canOperate && (
        <Tooltip
          isDisabled={
            device.state === 'PROVISIONED' && !!device.core.reportedVersion && !!device.ip
          }
          label={
            device.state !== 'PROVISIONED'
              ? 'Device has not been provisioned yet.'
              : 'Editing desired IP address requires the device to have reported its core version and IP address at least once.'
          }
        >
          <Box as="span">
            <EditButtons
              editDisabled={
                device.state !== 'PROVISIONED' || !device.core.reportedVersion || !device.ip
              }
              editLabel="Edit Desired IP Address"
              isEditing={false}
              isSaving={isSaving}
              onCancel={handleCancelEdit}
              onEdit={() =>
                handleStartEdit('ip', device.desiredIp ?? supportedChangeIpAddresses[0])
              }
              onSave={handleIpSave}
            />
          </Box>
        </Tooltip>
      )}
    </Flex>
  );
};

// ──── Section builders ────

const buildGeneralSection = ({
  device,
  canWrite,
  editState,
  serialNumber,
  deviceDashboardURL,
}: {
  device: DeviceResponse;
  canWrite: boolean;
  editState: FieldEditState;
  serialNumber: string | null | undefined;
  deviceDashboardURL: string;
}): DetailSectionProps => ({
  title: 'General',
  items: {
    ID: { body: device.id },
    'Mac Address': { body: <CopyableText value={device.macAddress} /> },
    'Serial Number': {
      body: serialNumber ? <CopyableText value={serialNumber} /> : 'Unregistered',
    },
    Connection: {
      body:
        device.state !== 'PROVISIONED' ? (
          'Not provisioned'
        ) : device.isConnected ? (
          <Tooltip
            label={`The device is currently online and connected at ${new Date(device.isConnectedUpdatedAt).toLocaleString(dateTimeFormat.local)}`}
          >
            <CloudAvailableBold color="green" />
          </Tooltip>
        ) : (
          <Tooltip
            label={`The device is currently offline and disconnected at ${new Date(device.isConnectedUpdatedAt).toLocaleString(dateTimeFormat.local)}`}
          >
            <CloudUnavailableBold color="grey" />
          </Tooltip>
        ),
    },
    State: {
      body: device.state === 'PROVISIONED' ? 'Provisioned' : 'Registered',
    },
    'Created At': {
      body: new Date(device.createdAt).toLocaleString(dateTimeFormat.local),
    },
    Workspace: { body: <WorkspaceTextLink workspaceId={device.workspaceId} /> },
    Fleets: {
      body: (
        <Flex flexWrap="wrap" gap={1}>
          {device.fleets.length === 0
            ? 'Unassigned'
            : device.fleets.map((fleet) => (
                <FleetNameBadge
                  fleetId={fleet.id}
                  isCohosted={fleet.workspaceId !== device.workspaceId}
                  key={fleet.id}
                  name={fleet.name}
                  workspaceId={fleet.workspaceId}
                />
              ))}
        </Flex>
      ),
    },
    ...(device.state === 'PROVISIONED'
      ? {
          'Active deployments': {
            body: (
              <Flex flexWrap="wrap" gap={1}>
                {device.deployments.length === 0
                  ? 'No active deployments'
                  : device.deployments.map((deployment) => (
                      <DeviceDeploymentBadge
                        deployment={deployment}
                        key={deployment.deploymentId}
                        workspaceId={
                          device.fleets.find((fleet) => fleet.id === deployment.fleetId)
                            ?.workspaceId
                        }
                      />
                    ))}
              </Flex>
            ),
          },
          'Reported Core Version': {
            documentationLink:
              'https://baseplate.legogroup.io/docs/default/component/emma/additional_information/core/changelog/',
            body: <ReportedCoreVersion device={device} />,
          },
          'Desired Core Version': {
            documentationLink:
              'https://baseplate.legogroup.io/docs/default/component/emma/additional_information/core/changelog/',
            body: (
              <DesiredCoreVersionField canWrite={canWrite} device={device} editState={editState} />
            ),
          },
          'Deployment Strategy': {
            documentationLink: deploymentStrategyDocURL,
            body: (
              <DeploymentStrategyField canWrite={canWrite} device={device} editState={editState} />
            ),
          },
          Observability: {
            body: (
              <Link href={deviceDashboardURL} isExternal p={0}>
                Device Dashboard
              </Link>
            ),
          },
        }
      : {}),
  },
});

const buildFirewallInfoSection = ({
  device,
  canOperate,
  editState,
  handleIpSave,
  isDhcpSaving,
  onDhcpToggleClick,
}: {
  device: DeviceResponse;
  canOperate: boolean;
  editState: FieldEditState;
  handleIpSave: () => Promise<void>;
  isDhcpSaving: boolean;
  onDhcpToggleClick: () => void;
}): DetailSectionProps => ({
  title: 'Networking',
  items: {
    'Reported IP': {
      body: (
        <Flex align="center" gap={2}>
          <Box as="span">{device.ip ?? 'Unknown'}</Box>
          {device.desiredIp == null ? null : device.ip === device.desiredIp ? (
            <Tooltip label="The device is running the desired IP address.">
              <StatusCircleCheck boxSize={5} color="green" mb={0.5} ml={2} />
            </Tooltip>
          ) : (
            <Tooltip
              label={`The device is not running the desired IP address. Desired: ${device.desiredIp}`}
            >
              <StatusInprogress boxSize={5} color="grey" mb={0.5} ml={2} />
            </Tooltip>
          )}
        </Flex>
      ),
    },
    'Desired IP': {
      body: (
        <DesiredIpField
          canOperate={canOperate}
          device={device}
          editState={editState}
          handleIpSave={handleIpSave}
        />
      ),
    },
    Netbox: {
      body: 'Non-Registered',
    },
    ...(canOperate &&
    device.os?.name === 'emmaOS' &&
    isOsVersionAtLeast(device.os?.version ?? '', '2.0.0') &&
    device.core.reportedVersion &&
    isOsVersionAtLeast(device.core.reportedVersion, '16.0.0')
      ? {
          'DHCP Enabled': {
            body: (
              <Flex align="center" gap={2}>
                <Box as="span">{device.dhcpEnabled ? 'Enabled' : 'Disabled'}</Box>
                <IconButton
                  aria-label="Edit DHCP"
                  color="LinkText"
                  icon={<Edit />}
                  isDisabled={isDhcpSaving}
                  isLoading={isDhcpSaving}
                  onClick={onDhcpToggleClick}
                  size="xs"
                  variant="solid"
                />
              </Flex>
            ),
          },
        }
      : {}),
  },
});

const buildFirewallLocationSection = ({
  device,
  canOperate,
  isRefreshingLocationData,
  onRefreshLocationData,
}: {
  device: DeviceResponse;
  canOperate: boolean;
  isRefreshingLocationData: boolean;
  onRefreshLocationData: () => Promise<void>;
}): DetailSectionProps => ({
  title: 'Firewall',
  items: {
    'Firewall Name': {
      body: (
        <>
          {device.firewallName ?? 'Unknown'}
          {device.firewallError ? (
            <Tooltip label={`Data might be unreliable: ${device.firewallError}`}>
              <StatusCircleExclamation boxSize={5} color="yellow.300" mb={0.5} ml={2} />
            </Tooltip>
          ) : null}
        </>
      ),
    },
    'Firewall Template': {
      body: device.firewallTemplate ? <CopyableText value={device.firewallTemplate} /> : 'Unknown',
    },
    'Workcenter ID': { body: device.workcenterId ?? 'Unassigned' },
    'Last Location Update': {
      body: new Date(device.firewallLocationInfoUpdatedAt).toLocaleString(dateTimeFormat.local),
    },
    ...(canOperate
      ? {
          '': {
            body: (
              <Tooltip
                isDisabled={device.state === 'PROVISIONED'}
                label="Device has not been provisioned yet."
              >
                <Box as="span">
                  <Button
                    cursor={
                      device.state !== 'PROVISIONED' || isRefreshingLocationData
                        ? 'not-allowed'
                        : 'pointer'
                    }
                    isDisabled={device.state !== 'PROVISIONED' || isRefreshingLocationData}
                    isLoading={isRefreshingLocationData}
                    leftIcon={
                      <Refresh
                        animation={isRefreshingLocationData ? `${spin} 1s linear infinite` : 'none'}
                        transition={'all 0.3s ease'}
                      />
                    }
                    onClick={onRefreshLocationData}
                    size="sm"
                    variant="outline"
                  >
                    Refresh Location Data
                  </Button>
                </Box>
              </Tooltip>
            ),
          },
        }
      : {}),
  },
});

const buildSystemInfoSection = ({
  device,
  canOperate,
  canWrite,
  editState,
  osVersionOptions,
  onUpgradeOs,
}: {
  device: DeviceResponse;
  canOperate: boolean;
  canWrite: boolean;
  editState: FieldEditState;
  osVersionOptions: { value: string; label: string }[];
  onUpgradeOs: (() => void) | undefined;
}): DetailSectionProps => ({
  title: 'System Information',
  items: {
    'Operating System': {
      body:
        device.state !== 'PROVISIONED' ? (
          'Not installed'
        ) : (
          <OsInfoBody
            canOperate={canOperate}
            canWrite={canWrite}
            device={device}
            editedValue={editState.editedValue}
            editingField={editState.editingField}
            handleCancelEdit={editState.handleCancelEdit}
            handleSave={editState.handleSave}
            handleStartEdit={editState.handleStartEdit}
            isSaving={editState.isSaving}
            onUpgradeOs={onUpgradeOs}
            osVersionOptions={osVersionOptions}
            setEditedValue={editState.setEditedValue}
          />
        ),
    },
    Vendor: { body: device.deviceType?.vendor ?? 'Unknown' },
    Model: { body: device.deviceType?.model ?? 'Unknown' },
    Architecture: { body: device.deviceType?.architecture ?? 'Unknown' },
    GPU: { body: device.deviceType?.gpu || 'None' },
    'CPU Model': { body: device.specs?.cpuModel ?? 'Not reported' },
    'CPU Cores': { body: device.specs?.cpuCores ?? 'Not reported' },
    'GPU (Reported)': { body: device.specs?.gpuModel ?? 'Not reported' },
    'Total RAM': {
      body: device.specs?.totalRamMb != null ? `${device.specs.totalRamMb} MB` : 'Not reported',
    },
    'Total Disk': {
      body: device.specs?.totalDiskGb != null ? `${device.specs.totalDiskGb} GB` : 'Not reported',
    },
  },
});

export const DeviceDetails = ({ device, refresh, onUpgradeOs }: Props) => {
  const {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
    handleIpSave,
  } = useDeviceFieldEdit(device, refresh);
  const { isRefreshingLocationData, refreshFirewallLocationData } = useFirewallLocationRefresh(
    device.id,
    refresh
  );

  const osVersionOptions = useMemo(() => {
    const compatibleVersions = getCompatibleOsVersionsForDevice(device.deviceType);
    return [
      { value: 'latest', label: 'Latest' },
      ...compatibleVersions.map((version) => ({
        value: version,
        label: version,
      })),
    ];
  }, [device.deviceType]);

  const workspaceQuery = useWorkspace(device.workspaceId, {
    showForbiddenErrorToast: false,
  });
  const canWrite = useCanWrite(workspaceQuery.data);
  const canOperate = useCanOperate(workspaceQuery.data);

  const toast = useToast();
  const { mutate } = useSWRConfig();
  const updateDevice = useUpdateDevice(device.id);
  const cacheKey = `devices/${device.id}`;

  const [showDhcpDialog, setShowDhcpDialog] = useState(false);
  const [isDhcpSaving, setIsDhcpSaving] = useState(false);
  const [dismissedRolloutWindowWarningDeviceIds, setDismissedRolloutWindowWarningDeviceIds] =
    useState<number[]>(getDismissedRolloutWindowWarningDeviceIds);

  const { data: osRolloutWindowsData } = useOsRolloutWindows(
    device.workspaceId,
    device.id,
    isOsRolloutWindowWarningCandidate(device)
  );

  const handleDhcpToggle = useCallback(async () => {
    setIsDhcpSaving(true);
    setShowDhcpDialog(false);
    try {
      const updatedDevice = await updateDevice({
        dhcpEnabled: !device.dhcpEnabled,
      });
      await mutate(cacheKey, updatedDevice, { revalidate: false });
      toast(
        ToastOptions.success(
          'DHCP updated',
          `DHCP has been ${updatedDevice.dhcpEnabled ? 'enabled' : 'disabled'}.`,
          3000
        )
      );
      refresh();
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsDhcpSaving(false);
    }
  }, [device.dhcpEnabled, updateDevice, mutate, cacheKey, refresh, toast]);

  const { data: deviceNetworkConfigsData, error: deviceNetworkConfigsError } =
    useDeviceNetworkConfigs(device.workspaceId, device.id, {
      showForbiddenErrorToast: false,
    });
  const { data: workspaceNetworkConfigsData, error: workspaceNetworkConfigsError } =
    useWorkspaceNetworkConfigs(device.workspaceId, {}, { showForbiddenErrorToast: false });

  const configsByInterface = useMemo(() => {
    if (deviceNetworkConfigsError || workspaceNetworkConfigsError) return undefined;
    return buildConfigsByInterface(
      deviceNetworkConfigsData?.deviceNetworkConfigs,
      workspaceNetworkConfigsData?.networkConfigs
    );
  }, [
    deviceNetworkConfigsData,
    deviceNetworkConfigsError,
    workspaceNetworkConfigsData,
    workspaceNetworkConfigsError,
  ]);

  const dismissRolloutWindowWarning = useCallback(() => {
    if (dismissedRolloutWindowWarningDeviceIds.includes(device.id)) return;

    const updatedDismissedDeviceIds = [...dismissedRolloutWindowWarningDeviceIds, device.id].slice(
      // Keep the newest dismissed device IDs and evict the oldest when we hit the cap.
      -MAX_DISMISSED_ROLLOUT_WINDOW_WARNING_DEVICE_IDS
    );
    setDismissedRolloutWindowWarningDeviceIds(updatedDismissedDeviceIds);
    localStorage.setItem(
      ROLLOUT_WINDOW_WARNING_DISMISSED_DEVICES_KEY,
      JSON.stringify(updatedDismissedDeviceIds)
    );
  }, [dismissedRolloutWindowWarningDeviceIds, device.id]);

  if (workspaceQuery.isLoading || workspaceQuery.error) {
    return <DetailsBox additionalComponent={<Loader />} sections={[]} title="Device" />;
  }

  const serialNumber = device.serialNumber ?? null;
  const dashboardId = getDeviceDashboardId(device.core?.reportedVersion);
  const deviceDashboardURL = `${import.meta.env.VITE_ELASTIC_URL}/app/dashboards#/view/${dashboardId}?_g=(filters:!(('$state':(store:globalState),meta:(key:metadata.deviceId,params:(query:${device.id}),type:phrase),query:(match_phrase:(metadata.deviceId:${device.id})))),refreshInterval:(pause:!t,value:60000),time:(from:now-15m,to:now))`;

  const editState: FieldEditState = {
    editingField,
    editedValue,
    setEditedValue,
    isSaving,
    handleStartEdit,
    handleCancelEdit,
    handleSave,
  };

  const deviceDetailsSections = [
    buildGeneralSection({
      device,
      canWrite,
      editState,
      serialNumber,
      deviceDashboardURL,
    }),
    ...(device.state === 'PROVISIONED'
      ? [
          buildFirewallInfoSection({
            device,
            canOperate,
            editState,
            handleIpSave,
            isDhcpSaving,
            onDhcpToggleClick: () => setShowDhcpDialog(true),
          }),
          buildFirewallLocationSection({
            device,
            canOperate,
            isRefreshingLocationData,
            onRefreshLocationData: refreshFirewallLocationData,
          }),
        ]
      : []),
    buildSystemInfoSection({
      device,
      canOperate,
      canWrite,
      editState,
      osVersionOptions,
      onUpgradeOs,
    }),
  ] as DetailSectionProps[];

  const isRolloutWindowWarningDismissed = dismissedRolloutWindowWarningDeviceIds.includes(
    device.id
  );
  const showRolloutWindowWarning =
    device.state === 'PROVISIONED' &&
    shouldShowRolloutWindowWarning(device) &&
    !isRolloutWindowWarningDismissed;

  const showOsRolloutWindowWarning = shouldShowOsRolloutWindowWarning(
    device,
    osRolloutWindowsData?.rolloutWindows
  );

  return (
    <>
      <ConfirmationDialog
        body={`${device.dhcpEnabled ? 'Disabling' : 'Enabling'} DHCP will change the device's network configuration. The device may become temporarily unreachable. Are you sure?`}
        header={`${device.dhcpEnabled ? 'Disable' : 'Enable'} DHCP on this device?`}
        isOpen={showDhcpDialog}
        onClose={() => setShowDhcpDialog(false)}
        onConfirmed={handleDhcpToggle}
      />
      <Box background="white" boxShadow="md" padding={6} rounded="md" width={{ base: '100%' }}>
        <DeviceNameHeader canOperate={canOperate} device={device} editState={editState} />
        {device.state === 'REGISTERED' && <DeviceInstallation device={device} />}
        {showRolloutWindowWarning && (
          <InlineNotification mb={6} variant="warning">
            <InlineNotification.Content>
              <InlineNotification.Title>Rollout window may be missing</InlineNotification.Title>
              <InlineNotification.Description>
                This device uses the schedule deployment strategy. Change the deployment strategy
                below or{' '}
                <Link as={ReactRouterLink as never} to={paths.deviceRolloutWindows(device.id)}>
                  create a rollout window
                </Link>
                .
              </InlineNotification.Description>
            </InlineNotification.Content>
            <CloseButton onClick={dismissRolloutWindowWarning} />
          </InlineNotification>
        )}
        {showOsRolloutWindowWarning && (
          <InlineNotification mb={6} variant="warning">
            <InlineNotification.Content>
              <InlineNotification.Title>OS update will not be applied</InlineNotification.Title>
              <InlineNotification.Description>
                The desired OS version ({device.os?.desiredVersion}) differs from the running
                version. With the {getDeploymentStrategyLabel(device.deploymentStrategy)} deployment
                strategy, OS updates are only applied in an active rollout window that includes OS
                updates, and this device has none. Change the deployment strategy or{' '}
                <Link as={ReactRouterLink as never} to={paths.deviceRolloutWindows(device.id)}>
                  create an Application and OS rollout window
                </Link>
                .
              </InlineNotification.Description>
            </InlineNotification.Content>
          </InlineNotification>
        )}
        {deviceDetailsSections.map((section, idx) => (
          <Box
            border={section.title ? '2px solid' : undefined}
            borderColor={section.title ? 'lightgrey' : undefined}
            borderRadius={section.title ? 'md' : undefined}
            key={section.title || `section-${idx}`}
            mb={6}
            p={section.title ? 5 : undefined}
          >
            {section.title && (
              <Text as="h4" fontSize="lg" fontWeight="semibold" mb={4}>
                {section.title}
              </Text>
            )}
            <Grid gap={4} templateColumns="repeat(auto-fill, minmax(300px, 1fr))">
              {Object.entries(section.items).map(([key, value]) => (
                <Grid.Item key={key}>
                  <Flex direction="row" gap={1}>
                    <Text fontWeight="medium">{key}</Text>
                    <DocumentationLink url={value.documentationLink} />
                  </Flex>
                  <Text as="div">{value.body}</Text>
                </Grid.Item>
              ))}
            </Grid>
            {section.title === 'Networking' && (
              <Box mt={6}>
                <DeviceNetworkInterfaces
                  configsByInterface={configsByInterface}
                  networkConfigsPath={configsByInterface && paths.deviceNetworkConfigs(device.id)}
                  networkInterfaces={device.networkInterfaces}
                  show="physical"
                />
              </Box>
            )}
          </Box>
        ))}
        <DeviceNetworkInterfaces
          configsByInterface={configsByInterface}
          networkConfigsPath={configsByInterface && paths.deviceNetworkConfigs(device.id)}
          networkInterfaces={device.networkInterfaces}
          show="vlan"
        />
      </Box>
    </>
  );
};
